/**
 * แผนที่โลกแบบภาพ — ผังโซนทั้งหมด (เชื่อมตามประตู) และแผนที่ใหญ่ของโซนปัจจุบัน
 *
 * ผังโลกวางจากเมืองออกไปตามประตู: east/west = ซ้ายขวา · ประตูที่อยู่ขอบบน/ล่าง (ถ้ำ ดันเจี้ยน) = ขึ้นลง
 * ไม่มีพิกัดในข้อมูล คิดจากทิศของประตูเอง เพิ่มโซนใน zones.json แล้วผังขยายเอง
 *
 * แผนที่โซนวาดทางเดินจริงจาก `routeNetwork` (shared/terrain.ts ตัวเดียวกับที่ server หนีบการเดิน)
 * วงมอน (สีตามธาตุ) ประตู และจุด "คุณอยู่ที่นี่"
 */
import {
  MONSTERS, ROUTE_HALF, routeNetwork, zoneHalf, type ZoneDef,
} from "@wickhaven/shared";
import { ELEMENT_GLOW } from "./element-colors.js";

export interface Cell { gx: number; gy: number }

/** ทิศของประตูบนผังโลก จากตำแหน่งประตูในโซน (ขอบไหนของแผนที่) */
function portalDir(x: number, z: number): [number, number] {
  return Math.abs(x) >= Math.abs(z) ? [Math.sign(x), 0] : [0, -Math.sign(z)];
}

/**
 * วางทุกโซนบนตาราง (BFS จากเมือง) ช่องชนกันเลื่อนออกไปทางเดิมจนว่าง
 * pure function — มีเทสต์ว่าไม่มีสองโซนช่องเดียวกัน
 */
export function layoutWorld(zones: Readonly<Record<string, ZoneDef>>, startId: string): Record<string, Cell> {
  const pos: Record<string, Cell> = {};
  const used = new Set<string>();
  const key = (c: Cell): string => `${c.gx},${c.gy}`;
  const start = zones[startId] ? startId : Object.keys(zones)[0]!;
  pos[start] = { gx: 0, gy: 0 };
  used.add("0,0");
  const queue = [start];
  while (queue.length) {
    const id = queue.shift()!;
    const here = pos[id]!;
    for (const p of zones[id]?.portals ?? []) {
      if (pos[p.to] || !zones[p.to]) continue;
      const [dx, dy] = portalDir(p.x, p.z);
      let c = { gx: here.gx + dx, gy: here.gy + dy };
      // ชนช่องที่มีคนอยู่แล้ว: ดันออกตั้งฉาก (ดันเจี้ยนสองแห่งใต้โซนติดกัน)
      for (let k = 1; used.has(key(c)); k++) {
        c = dy === 0 ? { gx: here.gx + dx, gy: here.gy + (k % 2 ? k : -k) } : { gx: here.gx + (k % 2 ? k : -k), gy: here.gy + dy };
      }
      pos[p.to] = c;
      used.add(key(c));
      queue.push(p.to);
    }
  }
  // โซนที่ไม่มีประตูเชื่อม (ไม่ควรมี) วางต่อท้ายแถวล่าง
  let extra = 0;
  for (const id of Object.keys(zones)) if (!pos[id]) pos[id] = { gx: extra++, gy: 6 };
  return pos;
}
export interface TrackCell { col: number; row: number; branch: boolean }

/**
 * ผังโลกแบบ "เส้นทางตามเลเวล" — สายหลัก (ประตูซ้าย/ขวา) เรียงจากเมืองแรกไปทางตะวันออก
 * ตัดเป็นแถวละ `perRow` วนแบบงู (แถวคี่เดินย้อน) ต่อกันเป็นเส้นเดียวอ่านง่าย
 * โซนข้างที่เข้าจากประตูขอบบน/ล่าง (ถ้ำ ดันเจี้ยน) ห้อยใต้โซนแม่ในแถบกิ่งของแถวนั้น
 * ชนกันเลื่อนไปช่องว่างใกล้สุด · pure function — มีเทสต์ว่าไม่มีสองโซนช่องเดียวกัน
 */
export function layoutTrack(zones: Readonly<Record<string, ZoneDef>>, startId: string, perRow = 6): Record<string, TrackCell> {
  const out: Record<string, TrackCell> = {};
  const start = zones[startId] ? startId : Object.keys(zones)[0]!;
  const side = (p: { x: number; z: number }): boolean => Math.abs(p.x) >= Math.abs(p.z);
  const chain: string[] = [];
  const seen = new Set<string>();
  // สายหลัก: เดินตามประตูข้างที่ยังไม่เคยไป (ตะวันออกก่อน)
  const walk = (id: string): void => {
    if (seen.has(id) || !zones[id]) return;
    seen.add(id);
    chain.push(id);
    const next = (zones[id]!.portals ?? []).filter((p) => side(p) && !seen.has(p.to)).sort((a, b) => b.x - a.x);
    for (const p of next) walk(p.to);
  };
  walk(start);
  chain.forEach((id, i) => {
    const row = Math.floor(i / perRow), k = i % perRow;
    out[id] = { row, col: row % 2 ? perRow - 1 - k : k, branch: false };
  });
  const used = new Set<string>();
  const place = (id: string, row: number, col: number): void => {
    for (let d = 0; d < perRow; d++) {
      for (const c of d ? [col + d, col - d] : [col]) {
        if (c < 0 || c >= perRow || used.has(`${row},${c}`)) continue;
        used.add(`${row},${c}`);
        out[id] = { col: c, row, branch: true };
        return;
      }
    }
    place(id, row + 1, col);
  };
  for (const id of chain) {
    for (const p of zones[id]!.portals ?? []) {
      if (side(p) || out[p.to] || !zones[p.to]) continue;
      place(p.to, out[id]!.row, out[id]!.col);
    }
  }
  // โซนที่ไม่มีประตูเชื่อม (ไม่ควรมี) ต่อท้าย
  const lastRow = Math.max(0, ...Object.values(out).map((c) => c.row)) + 1;
  for (const id of Object.keys(zones)) if (!out[id]) place(id, lastRow, 0);
  return out;
}

/** กล่องของโซนบนผังโลก (พิกัด CSS px) — ใช้ hover/คลิก */
export interface ZoneBox { id: string; x: number; y: number; w: number; h: number }

export const TRACK_PER_ROW = 6;
const CELL_W = 116, MAIN_H = 44, BRANCH_H = 30, LANE_GAP = 8, ROW_GAP = 14, PAD = 14;
const ROW_H = MAIN_H + LANE_GAP + BRANCH_H + ROW_GAP;

/** ขนาดที่ผังโลกต้องใช้ (CSS px) */
export function trackSize(zones: Readonly<Record<string, ZoneDef>>, townId: string): { w: number; h: number } {
  const pos = layoutTrack(zones, townId, TRACK_PER_ROW);
  const rows = Math.max(...Object.values(pos).map((c) => c.row)) + 1;
  return { w: PAD * 2 + TRACK_PER_ROW * CELL_W, h: PAD * 2 + rows * ROW_H - ROW_GAP };
}

/** ตั้งขนาดจริงของ canvas ตาม devicePixelRatio ให้ตัวหนังสือคม คืน context ที่สเกลแล้ว (หน่วย CSS px) */
function sharpCtx(canvas: HTMLCanvasElement, w: number, h: number): CanvasRenderingContext2D | null {
  const dpr = typeof window !== "undefined" ? Math.min(2, window.devicePixelRatio || 1) : 1;
  const pw = Math.round(w * dpr), ph = Math.round(h * dpr);
  if (canvas.width !== pw || canvas.height !== ph) {
    canvas.width = pw;
    canvas.height = ph;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
  }
  const ctx = canvas.getContext("2d");
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function fitText(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(t + "…").width > max) t = t.slice(0, -1);
  return t + "…";
}

/** วาดผังโลก คืนกล่องของแต่ละโซน (main.ts ใช้ทำ hover/คลิกเลือกดูรายละเอียด) */
export function drawWorldGraph(
  canvas: HTMLCanvasElement,
  zones: Readonly<Record<string, ZoneDef>>,
  townId: string,
  hereId: string,
  myLevel: number,
  lit: readonly string[] = [],
  selected?: string,
  hover?: string,
): ZoneBox[] {
  const { w: W, h: H } = trackSize(zones, townId);
  const ctx = sharpCtx(canvas, W, H);
  if (!ctx) return [];
  const pos = layoutTrack(zones, townId, TRACK_PER_ROW);
  const boxes: Record<string, ZoneBox> = {};
  for (const [id, c] of Object.entries(pos)) {
    boxes[id] = {
      id,
      x: PAD + c.col * CELL_W + 5,
      y: PAD + c.row * ROW_H + (c.branch ? MAIN_H + LANE_GAP : 0),
      w: CELL_W - 10,
      h: c.branch ? BRANCH_H : MAIN_H,
    };
  }
  ctx.clearRect(0, 0, W, H);
  const mid = (b: ZoneBox): [number, number] => [b.x + b.w / 2, b.y + b.h / 2];

  // เส้นทาง: สายหลักหนา · กิ่ง (ถ้ำ/ดันเจี้ยน) เส้นประ · ขึ้นแถวใหม่อ้อมขอบนอก
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const z of Object.values(zones)) {
    for (const p of z.portals ?? []) {
      const a = boxes[z.id], b = boxes[p.to];
      if (!a || !b || p.to < z.id) continue;
      const branch = pos[z.id]!.branch || pos[p.to]!.branch;
      ctx.strokeStyle = branch ? "rgba(230,130,130,.6)" : "rgba(170,205,255,.5)";
      ctx.lineWidth = branch ? 2 : 4;
      ctx.setLineDash(branch ? [4, 4] : []);
      const [ax, ay] = mid(a), [bx, by] = mid(b);
      ctx.beginPath();
      if (branch) {
        // จากใต้โซนแม่ ลงไปหัวกล่องกิ่ง
        const [top, low] = pos[z.id]!.branch ? [b, a] : [a, b];
        const tx = top.x + top.w / 2, lx = low.x + low.w / 2, yMid = top.y + top.h + LANE_GAP / 2;
        ctx.moveTo(tx, top.y + top.h);
        ctx.lineTo(tx, yMid);
        ctx.lineTo(lx, yMid);
        ctx.lineTo(lx, low.y);
      } else if (ay !== by) {
        const ex = ax < W / 2 ? PAD / 2 : W - PAD / 2;
        ctx.moveTo(ax, ay);
        ctx.lineTo(ex, ay);
        ctx.lineTo(ex, by);
        ctx.lineTo(bx, by);
      } else {
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
      }
      ctx.stroke();
    }
  }
  ctx.setLineDash([]);

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const z of Object.values(zones)) {
    const b = boxes[z.id];
    if (!b) continue;
    const [lo, hi] = z.levelRange;
    const fit = !z.town && myLevel >= lo && myLevel <= hi;
    const far = !z.town && myLevel < lo - 5;
    const here = z.id === hereId;
    roundRect(ctx, b.x, b.y, b.w, b.h, 7);
    ctx.fillStyle = "#15161d";
    ctx.fill();
    ctx.fillStyle = z.ground ?? "#5f8f4e";
    ctx.globalAlpha = far ? 0.28 : z.id === hover ? 0.85 : 0.55;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.lineWidth = here || z.id === selected ? 2.5 : 1.2;
    ctx.strokeStyle = here ? "#f5c542" : z.id === selected ? "#ffffff" : fit ? "#7ddc7d" : z.isDungeon ? "#e07a7a" : "rgba(255,255,255,.3)";
    ctx.stroke();
    const icon = z.town ? "🏘 " : z.isDungeon ? "⚔ " : "";
    const lvText = z.town ? "เมือง" : `Lv ${lo}–${hi}`;
    ctx.shadowColor = "#000";
    ctx.shadowBlur = 3;
    ctx.fillStyle = far ? "rgba(255,255,255,.6)" : "#fff";
    if (b.h >= MAIN_H) {
      ctx.font = "bold 12px sans-serif";
      ctx.fillText(fitText(ctx, icon + z.name, b.w - 12), b.x + b.w / 2, b.y + 15);
      ctx.font = "11px sans-serif";
      ctx.fillStyle = fit ? "#c6f7c6" : "rgba(255,255,255,.8)";
      ctx.fillText(lvText + (lit.includes(z.id) ? " 🏮" : ""), b.x + b.w / 2, b.y + 31);
    } else {
      ctx.font = "bold 10.5px sans-serif";
      ctx.fillText(fitText(ctx, icon + z.name, b.w - 10), b.x + b.w / 2, b.y + 10);
      ctx.font = "10px sans-serif";
      ctx.fillStyle = "rgba(255,255,255,.8)";
      ctx.fillText(lvText, b.x + b.w / 2, b.y + 22);
    }
    ctx.shadowBlur = 0;
  }
  // จุดคุณอยู่ที่นี่ (กะพริบด้วยเวลา)
  const hb = boxes[hereId];
  if (hb) {
    const pulse = 4.5 + Math.sin(performance.now() / 250) * 1.5;
    ctx.fillStyle = "#f5c542";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(hb.x + hb.w - 6, hb.y + 6, pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
  return Object.values(boxes);
}

/** แผนที่ใหญ่ของโซนปัจจุบัน: ทางเดิน วงมอน ประตู ตัวเรา (เหนือ = บนแผนที่ ไม่หมุนตามกล้อง) */
export function drawZoneMap(
  canvas: HTMLCanvasElement,
  zone: ZoneDef | undefined,
  me: { x: number; z: number } | undefined,
  extra: { names?: Readonly<Record<string, string>>; lantern?: { x: number; z: number } } = {},
): void {
  const W = Number(canvas.dataset["size"]) || canvas.clientWidth || 300;
  const ctx = sharpCtx(canvas, W, W);
  if (!ctx || !zone) return;
  const half = zoneHalf(zone);
  const k = W / (half * 2 + 8);
  // แกน: x โลก → ขวา · z โลก → ขึ้น (z มาก = บน)
  const X = (x: number): number => W / 2 + x * k;
  const Y = (z: number): number => W / 2 - z * k;
  ctx.clearRect(0, 0, W, W);
  ctx.fillStyle = zone.town ? zone.ground ?? "#6b9e5c" : "#1d2a1f";
  ctx.fillRect(0, 0, W, W);

  // ทางเดิน (เมืองเดินได้ทั่ว จึงทาทั้งผืน)
  if (!zone.town) {
    ctx.strokeStyle = zone.ground ?? "#6b9e5c";
    ctx.lineCap = "round";
    ctx.lineWidth = ROUTE_HALF * 2 * k;
    for (const [ax, az, bx, bz] of routeNetwork(zone)) {
      ctx.beginPath();
      ctx.moveTo(X(ax), Y(az));
      ctx.lineTo(X(bx), Y(bz));
      ctx.stroke();
    }
    ctx.fillStyle = zone.ground ?? "#6b9e5c";
    for (const sp of zone.spawns) {
      ctx.beginPath();
      ctx.arc(X(sp.x), Y(sp.z), (sp.radius + 4) * k, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  // วงมอน สีตามธาตุ บอสวงทอง
  for (const sp of zone.spawns) {
    const def = MONSTERS[sp.monster];
    ctx.fillStyle = ELEMENT_GLOW[def?.element ?? "neutral"];
    ctx.globalAlpha = 0.55;
    ctx.beginPath();
    ctx.arc(X(sp.x), Y(sp.z), Math.max(3, sp.radius * k * 0.7), 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    if (def?.boss) {
      ctx.strokeStyle = "#f5a142";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("👑", X(sp.x), Y(sp.z) - 8);
    }
  }
  // ประตู
  for (const p of zone.portals ?? []) {
    ctx.fillStyle = "#8fd8ff";
    ctx.beginPath();
    ctx.arc(X(p.x), Y(p.z), 5, 0, Math.PI * 2);
    ctx.fill();
    const name = extra.names?.[p.to];
    if (name) {
      // ป้ายชื่อปลายทาง ดันเข้ามาในกรอบ ไม่ตกขอบ
      ctx.font = "bold 11px sans-serif";
      const tw = ctx.measureText(name).width + 8;
      const lx = Math.min(W - tw / 2 - 2, Math.max(tw / 2 + 2, X(p.x)));
      const ly = Math.min(W - 10, Math.max(10, Y(p.z) + (p.z > 0 ? 14 : p.z < 0 ? -14 : -12)));
      ctx.fillStyle = "rgba(10,20,35,.8)";
      ctx.fillRect(lx - tw / 2, ly - 8, tw, 16);
      ctx.fillStyle = "#bfe9ff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(name, lx, ly);
    }
  }
  if (extra.lantern) {
    ctx.font = "14px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("🏮", X(extra.lantern.x), Y(extra.lantern.z));
  }
  // ตัวเรา
  if (me) {
    const pulse = 5 + Math.sin(performance.now() / 200) * 1.5;
    ctx.fillStyle = "#f5c542";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(X(me.x), Y(me.z), pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}
